import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  Pagination,
  Scorecard,
  ScorecardsActionStoreArgument,
  ScorecardStatus,
  ScorecardStores,
} from 'store/interfaces';
import api from 'api';
import {
  API_SCORECARD_OWN,
  API_SCORECARD_SEARCH,
  API_PROFILES_TEAM,
} from 'constants/api';
import qs from 'query-string';
import { RootState } from 'store/index';
import { storeName as subordinateStoreName } from 'store/scorecards/subordinates/config';
import { storeName as directReportsStoreName } from 'store/scorecards/direct/config';
import { storeName as ownScorecardsStoreName } from 'store/scorecards/own/config';
import { storeName as teamProfilesStoreName } from 'store/scorecards/profiles/config';
import { SCORECARD_STATUS_DELETED } from 'constants/scorecards';
import downloadBlob from 'helpers/downloadBlob';

function getAdditionalParameters(storeName: ScorecardsActionStoreArgument) {
  switch (storeName) {
    case subordinateStoreName:
      return { indirectSubordinates: 1 };
    case directReportsStoreName:
      return { subordinates: 1 };
    default:
      return {};
  }
}
export const updateList = createAsyncThunk<
  {
    list: Scorecard[];
    pagination: Pagination;
  } | void,
  {
    store: ScorecardStores;
    isArchived?: boolean;
    isDeleted?: boolean;
    additionalParams?: Record<string, any>;
    forcedStoreName?: string;
    export?: boolean;
    fileName?: string;
  }
>(
  'scorecards/updateList',
  async (
    {
      store,
      isArchived,
      isDeleted,
      additionalParams = {},
      forcedStoreName: initialForcedStoreName,
      export: isExport = false,
      fileName = 'scorecards_export',
    },
    thunkAPI,
  ) => {
    let forcedStoreName = initialForcedStoreName;
    const currentUrl = window.location.pathname;
    const isEditPage = currentUrl.includes('/edit');

    if (isEditPage) {
      forcedStoreName = undefined;
    }

    const {
      [store]: {
        filters,
        pagination: { page },
      },
    } = thunkAPI.getState() as RootState;

    let url =
      store === ownScorecardsStoreName
        ? API_SCORECARD_OWN
        : API_SCORECARD_SEARCH;

    if (
      store === teamProfilesStoreName ||
      forcedStoreName === teamProfilesStoreName
    ) {
      if (!isEditPage) {
        url = API_PROFILES_TEAM;
      }
    }

    const isArchivedParam = isArchived ? { archive: true } : {};
    const isDeletedParam = isDeleted
      ? {
          scorecardStatuses: SCORECARD_STATUS_DELETED as ScorecardStatus,
        }
      : {};

    const isDepartmentOverview =
      forcedStoreName === teamProfilesStoreName ? { department: true } : {};

    const queryParams = {
      ...getAdditionalParameters(store),
      ...filters,
      ...isArchivedParam,
      ...isDeletedParam,
      ...additionalParams,
      ...isDepartmentOverview,
      viewMode: null,
      ...(isExport ? { export: true } : { page }),
    };

    const requestConfig = isExport ? { responseType: 'blob' as const } : {};

    return api.axios
      .get(
        `${url}?${qs.stringify(queryParams, {
          skipNull: true,
          skipEmptyString: true,
        })}`,
        requestConfig,
      )
      .then((res) => {
        if (isExport) {
          downloadBlob(new Blob([res.data]), `${fileName}.xlsx`);
          return undefined;
        }
        return res.data;
      })
      .catch((e) => thunkAPI.rejectWithValue(e.response?.data));
  },
);
